import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from 'store'

// Define download status type
type DownloadStatus = 'downloading' | 'downloaded'

// Define a type for the slice state
interface CourseLectureDownloadStateReducer {
  loaded: boolean
  loading: boolean
  downloadStates: { [id: string]: DownloadStatus }
}

// Define the initial state using that type
const initialState: CourseLectureDownloadStateReducer = {
  loaded: false,
  loading: false,
  downloadStates: {}
}

export const courseLectureDownloadState = createSlice({
  name: 'courseLectureDownloadState',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setDownloadStates: (
      state,
      action: PayloadAction<{ [id: string]: DownloadStatus }>
    ) => {
      return {
        ...state,
        downloadStates: action.payload,
        loaded: true,
        loading: false
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      if (action.payload) return { ...state, loaded: false, loading: true }
      return { ...state, loaded: true, loading: false }
    },
    setDownloadState: (
      state,
      action: PayloadAction<{ id: string; status: DownloadStatus }>
    ) => {
      const downloadStates = { ...state.downloadStates }
      downloadStates[action.payload.id] = action.payload.status

      return { ...state, downloadStates }
    },
    clearDownloadStates: (state) => {
      return { ...state, downloadStates: {} }
    },
    setDownloading: (state, action: PayloadAction<string>) => {
      const downloadStates = { ...state.downloadStates }
      downloadStates[action.payload] = 'downloading'
      return { ...state, downloadStates }
    },
    setDownloaded: (state, action: PayloadAction<string>) => {
      const downloadStates = { ...state.downloadStates }
      downloadStates[action.payload] = 'downloaded'
      return { ...state, downloadStates }
    },
    removeDownloadState: (state, action: PayloadAction<string>) => {
      const downloadStates = { ...state.downloadStates }
      delete downloadStates[action.payload]
      return { ...state, downloadStates }
    }
  }
})

export const {
  setDownloadStates,
  setLoading,
  setDownloadState,
  clearDownloadStates,
  setDownloading,
  setDownloaded,
  removeDownloadState
} = courseLectureDownloadState.actions

export const selectDownloadLoadState = (state: RootState) => {
  return {
    loaded: state.courseLectureDownloadState.loaded,
    loading: state.courseLectureDownloadState.loading
  }
}

export const selectDownloadStates = (state: RootState) =>
  state.courseLectureDownloadState.downloadStates

export const selectDownloadState = (
  state: RootState,
  id: string
): DownloadStatus | undefined =>
  state.courseLectureDownloadState.downloadStates[id]

export const selectIsDownloading = (state: RootState, id: string): boolean =>
  state.courseLectureDownloadState.downloadStates[id] === 'downloading'

export const selectIsDownloaded = (state: RootState, id: string): boolean =>
  state.courseLectureDownloadState.downloadStates[id] === 'downloaded'

export default courseLectureDownloadState.reducer
